import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useCRM } from '../context/CRMContext';
import { subscribeCompanyLeadNotes } from '../firebase/firestore';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { loadFBSDK, fbGetLoginStatus, fbLogin, fetchAdAccounts, fetchActiveCampaignSpend } from '../utils/facebookSync';

// Platform-level FB App ID — same Firestore doc + fallback Integrations.js
// reads, so this page's own (separate, read-only) FB session uses the same
// app registration.
const PLATFORM_FB_APP_ID_FALLBACK = process.env.REACT_APP_FB_APP_ID || '1728065151881937';
// How often to re-poll Facebook for fresh spend while the Campaign
// Performance tab is open. Facebook's own reporting already lags real spend
// by a short window — this is "as live as Facebook will give us," via
// client-side polling rather than any push mechanism (no backend involved,
// consistent with the rest of this FB integration).
const SPEND_POLL_MS = 60000;

// ─── Digital Media — read-only lead-quality & campaign-performance view ──────
// Built for a "Digital Media" CRM role (an ads/marketing person) so they can
// see how leads from paid/digital channels are actually turning out — status,
// quality, and the notes counsellors have logged — without being able to edit
// anything here. The page itself is locked to that role (plus admins) in
// utils/access.js; this file only needs to worry about rendering, not gating.
//
// "Digital" sources = paid/owned channels where ad spend or campaign targeting
// is something this person could plausibly act on. Everything else (website,
// referral, walk-in, organic, manual, agent, event, email, whatsapp) is
// excluded because no campaign change affects it. Add to this list as more
// ad platforms get integrated.
const DIGITAL_SOURCES = ['facebook', 'instagram', 'google', 'linkedin', 'youtube'];

const SOURCE_LABELS = { facebook: 'Facebook', instagram: 'Instagram', google: 'Google Ads', linkedin: 'LinkedIn', youtube: 'YouTube' };
const SOURCE_ICONS  = { facebook: '📘', instagram: '📸', google: '🔍', linkedin: '💼', youtube: '▶️' };
const PRIORITY_COLORS = { hot: '#ef4444', warm: '#f59e0b', cold: '#3b82f6' };

function getDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}
function fmtDate(ts) {
  const d = getDate(ts);
  return d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

function downloadCSV(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map(row => row.map(v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"').join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const leadName = (l) => [l.firstName, l.lastName].filter(Boolean).join(' ') || l.name || l.email || l.phone || 'Unnamed lead';

function StatTile({ label, value, color }) {
  return (
    <div className="glass-card" style={{ padding: '14px 18px', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || '#0f172a' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function DigitalMedia() {
  const { leads, stages, companyId, integrations, saveIntegrations } = useCRM();
  const [tab, setTab] = useState('leads'); // 'leads' | 'campaigns'
  const [notes, setNotes] = useState([]);
  const [notesError, setNotesError] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');

  // ── Live FB ad-spend state ──────────────────────────────────────────────
  // fbStatus: 'checking' | 'connected' | 'disconnected' | 'error'
  const [platformAppId, setPlatformAppId] = useState(PLATFORM_FB_APP_ID_FALLBACK);
  const [fbStatus, setFbStatus] = useState('checking');
  const [fbToken, setFbToken] = useState(null);
  const [fbMissingAdsRead, setFbMissingAdsRead] = useState(false);
  const [fbError, setFbError] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [spend, setSpend] = useState({
    campaigns: [], fetchedAt: null, loading: false, error: null,
    accountSpendToday: null, accountSpendMonth: null, noInsightsAccess: false,
  });

  // ── Permanent "System User" token setup (so no one needs a personal FB
  // login at all) ───────────────────────────────────────────────────────────
  // A System User token, generated once in Facebook Business Manager and
  // scoped read-only to one ad account, isn't tied to any person's Facebook
  // session — it doesn't expire when someone logs out, and it works for
  // every viewer of this page. We store it the same way page access tokens
  // are already stored (Firestore, under integrations.facebook), so this is
  // the same trust boundary the app already uses for other credentials, not
  // a new one. No backend or app secret is involved on our side — Facebook
  // generates the token entirely inside Business Manager.
  const [showTokenSetup, setShowTokenSetup] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [acctIdInput, setAcctIdInput] = useState('');
  const [acctNameInput, setAcctNameInput] = useState('');
  const [acctCurrencyInput, setAcctCurrencyInput] = useState('INR');
  const [tokenSetupError, setTokenSetupError] = useState(null);
  const [tokenSetupBusy, setTokenSetupBusy] = useState(false);
  const [showTokenHelp, setShowTokenHelp] = useState(false);
  const [showRawCampaigns, setShowRawCampaigns] = useState(false);

  const savedFb = integrations?.facebook;
  const adAccountId = savedFb?.adAccountId || null;
  const adAccountName = savedFb?.adAccountName || '';
  const storedSpendToken = savedFb?.adSpendToken || null;
  // Prefer the permanent stored token; fall back to whatever personal FB
  // session this browser happens to have. Whichever one is live is what
  // actually fetches spend.
  const effectiveToken = storedSpendToken || fbToken;

  useEffect(() => {
    getDoc(doc(db, 'platformConfig', 'main'))
      .then(snap => {
        const appId = snap.exists() && snap.data().fbAppId;
        if (appId) setPlatformAppId(appId);
      })
      .catch(() => { /* use fallback */ });
  }, []);

  // Silently check for an existing FB session on mount — no login popup,
  // just "are we already authorised in this browser." Mirrors Integrations.js.
  useEffect(() => {
    let cancelled = false;
    loadFBSDK(platformAppId)
      .then(FB => fbGetLoginStatus(FB))
      .then(token => {
        if (cancelled) return;
        if (token) { setFbToken(token); setFbStatus('connected'); }
        else setFbStatus('disconnected');
      })
      .catch(() => { if (!cancelled) setFbStatus('error'); });
    return () => { cancelled = true; };
  }, [platformAppId]);

  // Holds the FB campaign IDs the CRM already knows about from real leads
  // (kept in sync with campaignRollup, which is computed further down this
  // component using leads loaded from CRMContext). A ref rather than a
  // dependency so refreshSpend doesn't need to be redefined every time leads
  // change — it just reads whatever the latest list is at call time.
  const knownCampaignIdsRef = useRef([]);

  const refreshSpend = React.useCallback(async () => {
    if (!effectiveToken || !adAccountId) return;
    setSpend(s => ({ ...s, loading: true, error: null }));
    try {
      const { campaigns, error, accountSpendToday, accountSpendMonth, noInsightsAccess, acctInsightsError } = await fetchActiveCampaignSpend(effectiveToken, adAccountId, knownCampaignIdsRef.current);
      if (error) setSpend(s => ({ ...s, loading: false, error: error.message || 'Facebook returned an error.' }));
      else setSpend({
        campaigns, fetchedAt: new Date(), loading: false, error: null,
        accountSpendToday, accountSpendMonth, noInsightsAccess,
        // A non-fatal account-level insights error still means we can't trust
        // the totals — fold it into the visible error rather than dropping it.
        ...(acctInsightsError ? { error: `Spend totals may be incomplete: ${acctInsightsError.message || 'Facebook insights error.'}` } : {}),
      });
    } catch (e) {
      setSpend(s => ({ ...s, loading: false, error: e.message || 'Could not reach Facebook.' }));
    }
  }, [effectiveToken, adAccountId]);

  // Fetch on connect + whenever the account/token changes, then poll every
  // 60s for as long as the Campaign Performance tab stays open.
  const refreshSpendRef = useRef(refreshSpend);
  useEffect(() => { refreshSpendRef.current = refreshSpend; }, [refreshSpend]);
  useEffect(() => {
    if (!effectiveToken || !adAccountId || tab !== 'campaigns') return;
    refreshSpendRef.current();
    const id = setInterval(() => refreshSpendRef.current(), SPEND_POLL_MS);
    return () => clearInterval(id);
  }, [effectiveToken, adAccountId, tab]);

  const handleConnectFB = async () => {
    setFbError(null);
    setFbMissingAdsRead(false);
    try {
      const FB = await loadFBSDK(platformAppId);
      const { accessToken, grantedScopes } = await fbLogin(FB);
      setFbToken(accessToken);
      setFbStatus('connected');
      // ads_read is the one permission the live spend view can't work
      // without. A user can untick it in FB's own consent dialog even though
      // we requested it — if that happened, campaigns will still load (that
      // only needs pages/business permissions) but every spend number will
      // come back empty, which otherwise looks identical to "real zero spend."
      if (!grantedScopes.includes('ads_read')) setFbMissingAdsRead(true);
      if (!adAccountId) await openAccountPicker(accessToken);
    } catch (e) {
      setFbError(e.message || 'Facebook login failed.');
    }
  };

  const openAccountPicker = async (tokenOverride) => {
    const token = tokenOverride || fbToken;
    if (!token) return;
    setPickerLoading(true);
    setShowPicker(true);
    try {
      const { accounts, error } = await fetchAdAccounts(token);
      if (error) setFbError(error.message || 'Could not load ad accounts.');
      else setAdAccounts(accounts);
    } catch (e) {
      setFbError(e.message || 'Could not load ad accounts.');
    } finally {
      setPickerLoading(false);
    }
  };

  const handlePickAccount = (acc) => {
    saveIntegrations({ facebook: { ...savedFb, adAccountId: acc.id, adAccountName: acc.name, adAccountCurrency: acc.currency || 'INR' } });
    setShowPicker(false);
  };

  const openTokenSetup = () => {
    setTokenInput('');
    setAcctIdInput(adAccountId || '');
    setAcctNameInput(adAccountName || '');
    setAcctCurrencyInput(savedFb?.adAccountCurrency || 'INR');
    setTokenSetupError(null);
    setShowTokenSetup(true);
  };

  // Test the pasted token against the given ad account before saving it, so
  // a typo'd token/account ID fails loudly here instead of silently breaking
  // the live spend panel for everyone later.
  const handleSaveToken = async () => {
    const token = tokenInput.trim();
    const acctId = acctIdInput.trim().replace(/^act_/, '');
    if (!token || !acctId) { setTokenSetupError('Both the token and the ad account ID are required.'); return; }
    setTokenSetupBusy(true);
    setTokenSetupError(null);
    try {
      const { error, noInsightsAccess } = await fetchActiveCampaignSpend(token, acctId);
      if (error) {
        setTokenSetupError(error.message || 'Facebook rejected this token/account combination.');
        return;
      }
      // The token can list campaigns without being able to read their spend —
      // catch that here, at save time, instead of letting it silently produce
      // ₹0 everywhere after the fact with no obvious explanation.
      if (noInsightsAccess) {
        setTokenSetupError('This token can see the ad account but Facebook returned no spend data for it. Double-check the System User was assigned the "Analyst" role on this ad account and that the token has the ads_read permission, then try again.');
        return;
      }
      saveIntegrations({
        facebook: {
          ...savedFb,
          adSpendToken: token,
          adAccountId: acctId,
          adAccountName: acctNameInput.trim() || acctId,
          adAccountCurrency: acctCurrencyInput.trim() || 'INR',
        },
      });
      setShowTokenSetup(false);
    } catch (e) {
      setTokenSetupError(e.message || 'Could not reach Facebook to verify this token.');
    } finally {
      setTokenSetupBusy(false);
    }
  };

  const handleClearToken = () => {
    if (!window.confirm('Remove the saved ad-spend token? Live spend will stop working for everyone until a new token or personal Facebook login is connected.')) return;
    const next = { ...savedFb };
    delete next.adSpendToken;
    saveIntegrations({ facebook: next });
  };

  useEffect(() => {
    if (!companyId) { setNotes([]); return; }
    setNotesError(null);
    // Surface subscription failures instead of silently showing "no notes" —
    // a missing Firestore index or a rules problem looks IDENTICAL to "there
    // really are no notes" unless we say so explicitly.
    const unsub = subscribeCompanyLeadNotes(companyId, setNotes, (err) => {
      console.error('[DigitalMedia] notes subscription failed:', err);
      setNotesError(err?.message || 'Failed to load notes.');
    });
    return () => unsub && unsub();
  }, [companyId]);

  const notesByLead = useMemo(() => {
    const map = {};
    notes.forEach(n => { (map[n.leadId] = map[n.leadId] || []).push(n); });
    return map;
  }, [notes]);

  const digitalLeads = useMemo(
    () => (leads || []).filter(l => DIGITAL_SOURCES.includes(l.source)),
    [leads]
  );

  const stageById = useMemo(() => {
    const map = {};
    (stages || []).forEach(s => { map[s.id] = s; });
    return map;
  }, [stages]);

  // "Converted" = the stage NAME indicates a real positive outcome (enrolled /
  // admitted / converted), not "whichever stage happens to sort last."
  // Matches Reports.js's convention (the one place in this app that already
  // gets this right) — NOT Dashboard.js's older "last stage by order"
  // shortcut, which silently miscounts leads as "converted" the moment a
  // pipeline appends a terminal stage like "Lost"/"Disqualified" after
  // "Enrolled" in its order — that stage would then sort last and get
  // credited as a win even though it's the opposite.
  const positiveStageIds = useMemo(() => {
    return new Set(
      (stages || [])
        .filter(s => {
          const n = (s.name || '').toLowerCase();
          return n.includes('enroll') || n.includes('admit') || n.includes('convert');
        })
        .map(s => s.id)
    );
  }, [stages]);
  const positiveStageNames = useMemo(
    () => (stages || []).filter(s => positiveStageIds.has(s.id)).map(s => s.name).join(' / '),
    [stages, positiveStageIds]
  );

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return digitalLeads.filter(l => {
      if (sourceFilter !== 'all' && l.source !== sourceFilter) return false;
      if (priorityFilter !== 'all' && (l.priority || 'cold') !== priorityFilter) return false;
      if (stageFilter !== 'all' && l.status !== stageFilter) return false;
      if (q) {
        const hay = (leadName(l) + ' ' + (l.email || '') + ' ' + (l.phone || '') + ' ' + (l.fbCampaignName || '')).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [digitalLeads, sourceFilter, priorityFilter, stageFilter, search]);

  const stats = useMemo(() => {
    const s = { total: digitalLeads.length, hot: 0, warm: 0, cold: 0, campaigns: new Set() };
    digitalLeads.forEach(l => {
      const p = l.priority || 'cold';
      s[p] = (s[p] || 0) + 1;
      if (l.fbCampaignName) s.campaigns.add(l.fbCampaignName);
    });
    return s;
  }, [digitalLeads]);

  const campaignRollup = useMemo(() => {
    const groups = {};
    digitalLeads.forEach(l => {
      const key = l.fbCampaignName || `No campaign tag — ${SOURCE_LABELS[l.source] || l.source}`;
      if (!groups[key]) groups[key] = { campaign: key, campaignId: '', source: l.source, total: 0, hot: 0, warm: 0, cold: 0, won: 0 };
      const g = groups[key];
      g.total++;
      const p = l.priority || 'cold';
      g[p] = (g[p] || 0) + 1;
      if (positiveStageIds.has(l.status)) g.won++;
      // Carry the FB campaign ID through too (every lead synced via the FB
      // form import already captures fbCampaignId — see mapFBLead in
      // utils/facebookSync.js) so spend can be joined on the exact ID rather
      // than the display name.
      if (!g.campaignId && l.fbCampaignId) g.campaignId = l.fbCampaignId;
    });
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [digitalLeads, positiveStageIds]);

  // Keep knownCampaignIdsRef (declared up near refreshSpend) in sync so the
  // next spend refresh can ask Facebook for these specific campaigns even if
  // they've since dropped out of the ACTIVE list.
  useEffect(() => {
    knownCampaignIdsRef.current = campaignRollup.map(g => g.campaignId).filter(Boolean);
  }, [campaignRollup]);

  // Join live FB spend onto the lead-derived rollup. Prefer the FB campaign
  // ID — it's an exact, stable key on both sides (leads carry fbCampaignId
  // from the original FB lead-ad payload; Facebook's campaigns API returns
  // the same id) — and only fall back to matching by display name for older
  // leads synced before fbCampaignId was captured. Name matching is fragile:
  // a campaign rename, a stray space, or a different dash character is
  // enough to silently break the join and show "—" even though the spend
  // data fetched from Facebook was perfectly fine (this is what was actually
  // happening — most campaigns matched and showed real spend, but the one
  // with real CRM leads had a name that no longer matched exactly).
  const spendById = useMemo(() => {
    const map = {};
    spend.campaigns.forEach(c => { if (c.id) map[c.id] = c; });
    return map;
  }, [spend.campaigns]);
  const spendByName = useMemo(() => {
    const map = {};
    spend.campaigns.forEach(c => { map[(c.name || '').trim().toLowerCase()] = c; });
    return map;
  }, [spend.campaigns]);

  const campaignRollupWithSpend = useMemo(() => {
    return campaignRollup.map(g => {
      const s = (g.campaignId && spendById[g.campaignId]) || spendByName[(g.campaign || '').trim().toLowerCase()] || null;
      return {
        ...g,
        matchedById: !!(g.campaignId && spendById[g.campaignId]),
        spendToday: s ? s.spendToday : null,
        spendMonth: s ? s.spendMonth : null,
        costPerLead: s && s.spendMonth != null && g.total > 0 ? s.spendMonth / g.total : null,
      };
    });
  }, [campaignRollup, spendById, spendByName]);

  // Active FB campaigns that ARE spending money but have no matching CRM
  // leads at all yet — a useful early-warning signal (lead sync broken,
  // campaign too new, or a naming mismatch) that would otherwise be invisible.
  // Excludes anything already matched by ID above, even if its name also
  // happens to differ from what's stored on the leads.
  const unmatchedSpendCampaigns = useMemo(() => {
    const knownIds = new Set(campaignRollup.map(g => g.campaignId).filter(Boolean));
    const knownNames = new Set(campaignRollup.map(g => (g.campaign || '').trim().toLowerCase()));
    return spend.campaigns.filter(c => !knownIds.has(c.id) && !knownNames.has((c.name || '').trim().toLowerCase()));
  }, [spend.campaigns, campaignRollup]);

  // Prefer the account-level /insights totals (a single simple call) over
  // summing the per-campaign nested-field numbers — if the nested expansion
  // came back empty for permission reasons while the account-level call
  // still worked, this keeps the stat tiles accurate instead of showing ₹0.
  // Only fall back to summing per-campaign numbers if the account-level call
  // itself didn't return anything either.
  const totalSpendToday = useMemo(() => {
    if (spend.accountSpendToday != null) return spend.accountSpendToday;
    if (spend.campaigns.some(c => c.spendToday != null)) return spend.campaigns.reduce((sum, c) => sum + (c.spendToday || 0), 0);
    return null;
  }, [spend.campaigns, spend.accountSpendToday]);
  const totalSpendMonth = useMemo(() => {
    if (spend.accountSpendMonth != null) return spend.accountSpendMonth;
    if (spend.campaigns.some(c => c.spendMonth != null)) return spend.campaigns.reduce((sum, c) => sum + (c.spendMonth || 0), 0);
    return null;
  }, [spend.campaigns, spend.accountSpendMonth]);
  const spendCurrency = savedFb?.adAccountCurrency || 'INR';
  const fmtMoney = (n) => {
    if (n == null) return '—';
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: spendCurrency, maximumFractionDigits: 0 }).format(n); }
    catch { return spendCurrency + ' ' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
  };

  const exportLeadsCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Source', 'Campaign', 'Ad Set', 'Ad', 'Form', 'Stage', 'Priority', 'Notes Count', 'Date Added'];
    const rows = filteredLeads.map(l => [
      leadName(l), l.email || '', l.phone || '', SOURCE_LABELS[l.source] || l.source,
      l.fbCampaignName || '', l.fbAdsetName || '', l.fbAdName || '', l.fbFormName || '',
      stageById[l.status]?.name || l.status || '', l.priority || '',
      (notesByLead[l.id] || []).length, fmtDate(l.createdAt),
    ]);
    downloadCSV(`digital_leads_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const exportNotesCSV = (leadsSubset, filenamePrefix) => {
    const headers = ['Lead Name', 'Source', 'Campaign', 'Note', 'Added By', 'Date'];
    const rows = [];
    leadsSubset.forEach(l => {
      (notesByLead[l.id] || []).forEach(n => {
        rows.push([leadName(l), SOURCE_LABELS[l.source] || l.source, l.fbCampaignName || '', n.content || '', n.createdBy || '', fmtDate(n.createdAt)]);
      });
    });
    if (rows.length === 0) { window.alert('No notes to download for this selection yet.'); return; }
    downloadCSV(`${filenamePrefix}_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const exportCampaignCSV = () => {
    const headers = ['Campaign', 'Source', 'Total Leads', 'Hot', 'Warm', 'Cold', 'Reached Final Stage', 'Conversion %', 'Spend Today', 'Spend This Month', 'Cost Per Lead'];
    const rows = campaignRollupWithSpend.map(g => [
      g.campaign, SOURCE_LABELS[g.source] || g.source, g.total, g.hot, g.warm, g.cold, g.won,
      g.total ? ((g.won / g.total) * 100).toFixed(1) : '0.0',
      g.spendToday == null ? '' : g.spendToday.toFixed(2),
      g.spendMonth == null ? '' : g.spendMonth.toFixed(2),
      g.costPerLead == null ? '' : g.costPerLead.toFixed(2),
    ]);
    downloadCSV(`campaign_performance_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const tabBtn = (id, label) => (
    <button
      onClick={() => setTab(id)}
      className={tab === id ? 'btn-primary' : 'btn-ghost'}
      style={{ fontSize: 13 }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>Digital Media</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
            Read-only view of leads from paid/digital channels — quality, status, and counsellor notes — to inform campaign decisions.
            This view cannot edit leads, notes, or stages.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tabBtn('leads', 'Leads')}
          {tabBtn('campaigns', 'Campaign Performance')}
        </div>
      </div>

      {notesError && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#b91c1c' }}>
          Couldn't load notes ({notesError}). Lead and campaign data above is still accurate, but note counts/content may be missing until this is fixed.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatTile label="Digital Leads" value={stats.total} />
        <StatTile label="Hot" value={stats.hot} color={PRIORITY_COLORS.hot} />
        <StatTile label="Warm" value={stats.warm} color={PRIORITY_COLORS.warm} />
        <StatTile label="Cold" value={stats.cold} color={PRIORITY_COLORS.cold} />
        <StatTile label="Campaigns Tracked" value={stats.campaigns.size} color="#7c3aed" />
      </div>

      {tab === 'leads' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="crm-input" placeholder="Search name, email, phone, campaign…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: '1 1 220px', minWidth: 200 }}
            />
            <select className="crm-select" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
              <option value="all">All sources</option>
              {DIGITAL_SOURCES.map(s => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
            </select>
            <select className="crm-select" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
              <option value="all">All quality</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
            <select className="crm-select" value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
              <option value="all">All stages</option>
              {(stages || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button className="btn-ghost" onClick={exportLeadsCSV}>⬇ Export Leads CSV</button>
            <button className="btn-ghost" onClick={() => exportNotesCSV(filteredLeads, 'digital_lead_notes')}>⬇ Export Notes CSV</button>
          </div>

          <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1.4fr 1fr 1fr 0.8fr 0.9fr', gap: 8, padding: '10px 16px', background: 'rgba(0,0,0,0.02)', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              <div>Lead</div><div>Source</div><div>Campaign / Ad</div><div>Stage</div><div>Quality</div><div>Notes</div><div>Added</div>
            </div>
            {filteredLeads.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No digital leads match these filters.</div>
            )}
            {filteredLeads.map(l => {
              const isOpen = expandedLead === l.id;
              const leadNotes = notesByLead[l.id] || [];
              return (
                <div key={l.id} style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <div
                    onClick={() => setExpandedLead(isOpen ? null : l.id)}
                    style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1.4fr 1fr 1fr 0.8fr 0.9fr', gap: 8, padding: '12px 16px', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{leadName(l)}</div>
                    <div style={{ fontSize: 13 }}>{SOURCE_ICONS[l.source] || ''} {SOURCE_LABELS[l.source] || l.source}</div>
                    <div style={{ fontSize: 12, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.fbCampaignName || ''}>
                      {l.fbCampaignName || <span style={{ color: '#cbd5e1' }}>—</span>}
                    </div>
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: (stageById[l.status]?.color || '#94a3b8') + '20', color: stageById[l.status]?.color || '#64748b' }}>
                        {stageById[l.status]?.name || l.status || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, textTransform: 'capitalize', background: (PRIORITY_COLORS[l.priority] || PRIORITY_COLORS.cold) + '20', color: PRIORITY_COLORS[l.priority] || PRIORITY_COLORS.cold }}>
                        {l.priority || 'cold'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{leadNotes.length} {isOpen ? '▲' : '▼'}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(l.createdAt)}</div>
                  </div>
                  {isOpen && (
                    <div style={{ padding: '4px 16px 16px 16px', background: 'rgba(0,0,0,0.015)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Notes ({leadNotes.length})</div>
                        <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={(e) => { e.stopPropagation(); exportNotesCSV([l], `notes_${leadName(l).replace(/\s+/g, '_')}`); }}>
                          ⬇ Download these notes
                        </button>
                      </div>
                      {leadNotes.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8' }}>No notes logged for this lead yet.</div>}
                      {leadNotes.map(n => (
                        <div key={n.id} style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', marginBottom: 6, border: '1px solid rgba(0,0,0,0.05)', borderLeft: '3px solid #7c3aed' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{n.createdBy || 'Unknown'}</span>
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDate(n.createdAt)}</span>
                          </div>
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.content}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'campaigns' && (
        <div>
          {/* ── Live FB ad-spend panel ──────────────────────────────────── */}
          <div className="glass-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: adAccountId ? 12 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                📊 Live Facebook Ad Spend
                {storedSpendToken && <span style={{ fontWeight: 600, fontSize: 11, color: '#10b981', marginLeft: 8 }}>● Connected for everyone</span>}
              </div>

              {/* Permanent token is set — this works for every viewer, no personal login needed. */}
              {storedSpendToken && adAccountId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {spend.loading ? 'Refreshing…' : spend.fetchedAt ? `Updated ${spend.fetchedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                  </span>
                  <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={refreshSpend} disabled={spend.loading}>↻ Refresh</button>
                  <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={openTokenSetup}>Manage token</button>
                </div>
              )}

              {/* No permanent token yet — offer to set one up, with the personal-login flow as a fallback. */}
              {!storedSpendToken && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {fbToken && adAccountId && (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                      {spend.loading ? 'Refreshing…' : spend.fetchedAt ? `Updated ${spend.fetchedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} (your session)` : ''}
                    </span>
                  )}
                  {fbToken && adAccountId && (
                    <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={refreshSpend} disabled={spend.loading}>↻ Refresh</button>
                  )}
                  {fbToken && !adAccountId && (
                    <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => openAccountPicker()}>Browse my ad accounts</button>
                  )}
                  {!fbToken && fbStatus !== 'checking' && (
                    <button className="btn-ghost" style={{ fontSize: 12 }} onClick={handleConnectFB}>Use my Facebook login</button>
                  )}
                  <button className="btn-primary" style={{ fontSize: 12 }} onClick={openTokenSetup}>Set up permanent connection</button>
                </div>
              )}
            </div>

            {fbError && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#b91c1c', marginTop: 10 }}>
                {fbError}
              </div>
            )}

            {fbMissingAdsRead && (
              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400e', marginTop: 10 }}>
                ⚠ Facebook connected, but the <strong>ads_read</strong> permission wasn't granted — likely unticked in the login popup. Campaigns can still be listed, but spend will show as no data until you reconnect and approve it.{' '}
                <button type="button" className="btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }} onClick={handleConnectFB}>Reconnect &amp; approve</button>
              </div>
            )}

            {!fbMissingAdsRead && spend.noInsightsAccess && adAccountId && (
              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400e', marginTop: 10 }}>
                ⚠ This connection can see campaign names but Facebook isn't returning any spend numbers for them — that's a permissions gap, not zero spend. If using a System User token, confirm it has the "Analyst" role (or higher) on this ad account; if using your personal login, make sure you're an admin/advertiser on the Business Manager account.
              </div>
            )}

            {showPicker && (
              <div style={{ marginTop: 12, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: 12, background: 'rgba(0,0,0,0.015)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Which ad account should the CRM track?</div>
                {pickerLoading && <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading your ad accounts…</div>}
                {!pickerLoading && adAccounts.length === 0 && (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>No ad accounts visible to this Facebook login. Make sure you're logged in with a user that has access to the Business Manager ad account.</div>
                )}
                {!pickerLoading && adAccounts.map(acc => (
                  <div key={acc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: 8, background: '#fff', border: '1px solid rgba(0,0,0,0.05)', marginBottom: 6 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{acc.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{acc.id} · {acc.currency}</div>
                    </div>
                    <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => handlePickAccount(acc)}>Track this (this session only)</button>
                  </div>
                ))}
                <button className="btn-ghost" style={{ fontSize: 11, marginTop: 4 }} onClick={() => setShowPicker(false)}>Cancel</button>
              </div>
            )}

            {showTokenSetup && (
              <div style={{ marginTop: 12, border: '1px solid rgba(124,58,237,0.25)', borderRadius: 10, padding: 14, background: 'rgba(124,58,237,0.03)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Set up a permanent connection</div>
                <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 10px' }}>
                  Paste a Facebook <strong>System User</strong> token, scoped read-only to one ad account. Once saved, every Digital Media viewer sees live spend automatically — no one needs their own Facebook login.{' '}
                  <button type="button" className="btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setShowTokenHelp(s => !s)}>
                    {showTokenHelp ? 'Hide' : 'How do I get this token?'}
                  </button>
                </p>
                {showTokenHelp && (
                  <div style={{ fontSize: 11, color: '#475569', background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, padding: 10, marginBottom: 10, lineHeight: 1.6 }}>
                    In Facebook Business Manager: Business Settings → System Users → Add → name it something like "CRM Ad Spend Reader." Assign it to the ad account you want tracked with the "Analyst" role (read-only — avoid giving it ad-edit permissions). Then click Generate New Token, select this app, tick the <code>ads_read</code> permission, and set it to never expire if offered. Copy that token and paste it below, along with the ad account ID shown in Business Manager (the numeric ID, with or without the "act_" prefix).
                  </div>
                )}
                {tokenSetupError && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>
                    {tokenSetupError}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                  <input className="crm-input" placeholder="System User access token" value={tokenInput} onChange={e => setTokenInput(e.target.value)} type="password" />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input className="crm-input" style={{ flex: '1 1 160px' }} placeholder="Ad account ID (e.g. 123456789 or act_123456789)" value={acctIdInput} onChange={e => setAcctIdInput(e.target.value)} />
                    <input className="crm-input" style={{ flex: '1 1 160px' }} placeholder="Account name (for display)" value={acctNameInput} onChange={e => setAcctNameInput(e.target.value)} />
                    <input className="crm-input" style={{ flex: '0 1 100px' }} placeholder="Currency" value={acctCurrencyInput} onChange={e => setAcctCurrencyInput(e.target.value)} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button className="btn-primary" style={{ fontSize: 12 }} onClick={handleSaveToken} disabled={tokenSetupBusy}>{tokenSetupBusy ? 'Verifying…' : 'Verify & Save'}</button>
                  <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setShowTokenSetup(false)}>Cancel</button>
                  {storedSpendToken && (
                    <button className="btn-ghost" style={{ fontSize: 12, color: '#b91c1c', marginLeft: 'auto' }} onClick={handleClearToken}>Remove saved token</button>
                  )}
                </div>
              </div>
            )}

            {effectiveToken && adAccountId && !showPicker && !showTokenSetup && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <StatTile label={`Spent Today · ${adAccountName}`} value={fmtMoney(totalSpendToday)} color="#7c3aed" />
                <StatTile label="Spent This Month" value={fmtMoney(totalSpendMonth)} color="#7c3aed" />
                <StatTile label="Active Campaigns" value={spend.campaigns.filter(c => !c.notCurrentlyActive).length} />
                {spend.error && <div style={{ fontSize: 12, color: '#b91c1c', alignSelf: 'center' }}>Couldn't refresh spend: {spend.error}</div>}
                <button type="button" className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px', alignSelf: 'center' }} onClick={() => setShowRawCampaigns(s => !s)}>
                  {showRawCampaigns ? 'Hide' : 'Why is a campaign missing spend?'}
                </button>
              </div>
            )}

            {/* Side-by-side raw comparison — exists because campaign matching can
                fail silently for reasons invisible from the rolled-up table alone
                (a name that no longer matches exactly, a campaign that Facebook
                no longer reports as ACTIVE, or a lead synced before fbCampaignId
                was captured). Showing both lists raw lets you spot the mismatch
                in seconds instead of guessing. */}
            {showRawCampaigns && effectiveToken && adAccountId && (
              <div style={{ marginTop: 12, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: 12, background: 'rgba(0,0,0,0.015)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>CRM campaign groups (from your leads)</div>
                {campaignRollupWithSpend.map(g => (
                  <div key={g.campaign} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <span style={{ color: '#0f172a' }}>{g.campaign} <span style={{ color: '#94a3b8' }}>(id: {g.campaignId || 'not captured — older lead'})</span></span>
                    <span style={{ color: g.spendToday == null && g.spendMonth == null ? '#b91c1c' : '#10b981', fontWeight: 600 }}>
                      {g.spendToday == null && g.spendMonth == null ? 'no match in Facebook’s list below' : 'matched ✓'}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '12px 0 8px' }}>
                  Campaigns Facebook returned spend for ({spend.campaigns.length}) — currently-active ones, plus any CRM-tracked campaign fetched directly even if it's since paused or ended
                </div>
                {spend.campaigns.length === 0 && <div style={{ fontSize: 11, color: '#94a3b8' }}>None returned — either there are no active campaigns, or this token/login can't see this ad account's campaign list.</div>}
                {spend.campaigns.map(c => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <span style={{ color: '#0f172a' }}>
                      {c.name} <span style={{ color: '#94a3b8' }}>(id: {c.id})</span>
                      {c.notCurrentlyActive && <span style={{ color: '#b45309', marginLeft: 6 }}>· {c.effectiveStatus || 'not currently active'}</span>}
                    </span>
                    <span style={{ color: '#64748b' }}>{c.insightsAvailable ? `${fmtMoney(c.spendToday)} today` : 'no insights returned'}</span>
                  </div>
                ))}
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, marginBottom: 0 }}>
                  If your campaign's name/id still doesn't appear above at all, the connected token/login most likely can't see that specific campaign (e.g. it sits in a different ad account, or the System User wasn't assigned access to it) — worth confirming directly in Ads Manager.
                </p>
              </div>
            )}

            {!storedSpendToken && !fbToken && fbStatus !== 'checking' && !showTokenSetup && (
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, marginBottom: 0 }}>
                No live spend connection yet. "Use my Facebook login" works only for this browser/session — for something every Digital Media viewer can see without logging in themselves, set up a permanent connection instead.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn-ghost" onClick={exportCampaignCSV}>⬇ Export Campaign Performance CSV</button>
          </div>
          <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: adAccountId ? '1.7fr 0.6fr 0.6fr 0.6fr 0.6fr 0.8fr 0.9fr 0.8fr 0.8fr' : '2fr 0.8fr 0.7fr 0.7fr 0.7fr 1fr 1fr', gap: 8, padding: '10px 16px', background: 'rgba(0,0,0,0.02)', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              <div>Campaign</div><div>Total</div><div>Hot</div><div>Warm</div><div>Cold</div><div>Quality Mix</div><div>Conversion</div>
              {adAccountId && <><div>Spend (Today / MTD)</div><div>Cost/Lead</div></>}
            </div>
            {campaignRollupWithSpend.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No digital leads yet to summarize by campaign.</div>
            )}
            {campaignRollupWithSpend.map((g, i) => {
              const conv = g.total ? (g.won / g.total) * 100 : 0;
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: adAccountId ? '1.7fr 0.6fr 0.6fr 0.6fr 0.6fr 0.8fr 0.9fr 0.8fr 0.8fr' : '2fr 0.8fr 0.7fr 0.7fr 0.7fr 1fr 1fr', gap: 8, padding: '12px 16px', alignItems: 'center', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.campaign}>
                    {SOURCE_ICONS[g.source] || ''} {g.campaign}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{g.total}</div>
                  <div style={{ fontSize: 13, color: PRIORITY_COLORS.hot }}>{g.hot}</div>
                  <div style={{ fontSize: 13, color: PRIORITY_COLORS.warm }}>{g.warm}</div>
                  <div style={{ fontSize: 13, color: PRIORITY_COLORS.cold }}>{g.cold}</div>
                  <div>
                    <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#f1f5f9', width: '100%' }}>
                      {g.total > 0 && <div style={{ width: `${(g.hot / g.total) * 100}%`, background: PRIORITY_COLORS.hot }} />}
                      {g.total > 0 && <div style={{ width: `${(g.warm / g.total) * 100}%`, background: PRIORITY_COLORS.warm }} />}
                      {g.total > 0 && <div style={{ width: `${(g.cold / g.total) * 100}%`, background: PRIORITY_COLORS.cold }} />}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: conv >= 20 ? '#10b981' : conv >= 5 ? '#f59e0b' : '#64748b' }}>
                    {conv.toFixed(1)}% <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>({g.won}/{g.total})</span>
                  </div>
                  {adAccountId && (
                    <>
                      <div style={{ fontSize: 12, color: '#374151' }}>
                        {g.spendToday == null ? <span style={{ color: '#cbd5e1' }}>—</span> : <>{fmtMoney(g.spendToday)} <span style={{ color: '#94a3b8' }}>/ {fmtMoney(g.spendMonth)}</span></>}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed' }}>{g.costPerLead == null ? <span style={{ color: '#cbd5e1' }}>—</span> : fmtMoney(g.costPerLead)}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {adAccountId && unmatchedSpendCampaigns.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginBottom: 6 }}>
                ⚠ Active campaigns spending money with no matching CRM leads yet
              </div>
              <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                {unmatchedSpendCampaigns.map(c => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid rgba(0,0,0,0.05)', fontSize: 12 }}>
                    <span style={{ color: '#0f172a', fontWeight: 600 }}>{c.name}</span>
                    <span style={{ color: '#64748b' }}>{fmtMoney(c.spendToday)} today · {fmtMoney(c.spendMonth)} this month</span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                Either no leads have come in yet, the campaign just started, or its name doesn't match what's tagged on incoming leads — worth a quick check.
              </p>
            </div>
          )}

          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
            "Conversion" = leads currently sitting in {positiveStageNames ? `a stage named "${positiveStageNames}"` : 'an Enrolled/Admitted-type stage'}.
            {!positiveStageNames && ' No stage in this pipeline is named that way yet, so conversion will show 0% until one exists.'}
            {' '}Leads without a campaign tag are grouped under "No campaign tag" by source.
            {adAccountId && ' Spend figures come straight from Facebook for active campaigns only and refresh automatically while this tab is open.'}
          </p>
        </div>
      )}
    </div>
  );
}
